// components/reports/ReportShell.js
// Shared header/patient-card/footer chrome for the Opioid Risk / Pain
// Perception / PGX report templates — CRA/MUI port of the marketing site's
// version (plain inline styles instead of Tailwind, which isn't available
// in this app). Matches the same visual design (logo placement, #575555
// section bars, patient info card, Interpretation/Methodology/Disclaimer
// footer sections) as the real Proove report PDFs — minus the Proove
// Promise/signatures/lab-director footer block, deliberately never shown
// here: neither AIden/ClAIre's own AI interpretation nor a free-conversion
// of an old report is an official signed lab document.
import React, { useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { sectionBar, textBlock, innerCard } from "./reportStyles";

function ReferencesList({ items }) {
  return (
    <ol style={{ margin: 0, padding: "12px 16px", fontSize: "0.8rem", lineHeight: 1.6, color: "#575555" }}>
      {items.map((ref, i) => (
        <li key={i} style={{ display: "flex", gap: 6, listStyle: "none", marginBottom: 4 }}>
          <span style={{ flexShrink: 0, color: "#9a9a9a" }}>({i + 1})</span>
          <span>{ref}</span>
        </li>
      ))}
    </ol>
  );
}

export default function ReportShell({
  title,
  subtitle,
  patient,
  reportDate,
  children,
  interpretationTitle = "Interpretation",
  interpretationNode,
  methodology,
  disclaimer,
  references,
  // "official" = free conversion of a real old Proove report — looks exactly
  // like the authentic document. "analysis" (default) = AIden/ClAIre's own
  // ad-hoc AI interpretation — same structure, but visibly marked as such so
  // the two are never mistaken for each other at a glance.
  variant = "analysis",
  // "AIden" | "ClAIre" — whichever assistant generated this ad-hoc analysis;
  // ignored for variant="official". Badge reads e.g. "AIDEN ANALYSIS" so a
  // patient/clinician can tell at a glance which assistant produced it.
  analyzedBy,
}) {
  const rootRef = useRef(null);

  // Prints via a same-document iframe (react-to-print) instead of a blank
  // window.open() popup — the popup had no stylesheet of its own, so every
  // MUI/layout style was silently dropped from the printed output.
  const handlePrint = useReactToPrint({
    contentRef: rootRef,
    documentTitle: title || "Proove Report",
  });

  return (
    <div style={{ marginTop: 16, overflow: "hidden", borderRadius: 16, border: "1px solid #e2e2e2", background: "#fff", color: "#333" }}>
      {/* Scoped styling for the AI-generated summary HTML (dangerouslySetInnerHTML
          in each report component) — the model only outputs plain <b>/<ul>/<li>,
          which render as flat black-on-black bold with cramped default list
          spacing. This gives section sub-emphasis (risk levels, gene names,
          drug names, etc.) a real visual accent instead of looking identical
          to the surrounding body text, and gives bullets proper breathing room. */}
      <style>{`
        .ai-summary-html b { color: #0a0e27; font-weight: 700; }
        .ai-summary-html ul { margin: 6px 0 14px; padding-left: 22px; }
        .ai-summary-html li { margin-bottom: 8px; }
        .ai-summary-html li::marker { color: #637cdb; }
      `}</style>
      <div ref={rootRef}>
        <div style={{
          height: 4,
          background: variant === "official"
            ? "linear-gradient(to right, #0a0e27, #a1750f)"
            : "linear-gradient(to right, #0a0e27, #637cdb)",
        }} />
        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", borderBottom: "1px solid #e2e2e2", padding: "16px 20px" }}>
          <div>
            <img src="/logo.png" alt="Proove Genomics" style={{ height: 32, width: "auto" }} />
            {variant === "official" ? (
              <span style={{
                display: "inline-block", marginLeft: 10, verticalAlign: "middle",
                borderRadius: 999, padding: "2px 10px", fontSize: "0.74rem", fontWeight: 700,
                color: "#a1750f", background: "rgba(161,117,15,0.12)", border: "1px solid rgba(161,117,15,0.3)",
              }}>
                CONVERTED FROM ORIGINAL REPORT
              </span>
            ) : (
              <span style={{
                display: "inline-block", marginLeft: 10, verticalAlign: "middle",
                borderRadius: 999, padding: "2px 10px", fontSize: "0.74rem", fontWeight: 700,
                color: "#637cdb", background: "rgba(99,124,219,0.12)", border: "1px solid rgba(99,124,219,0.3)",
              }}>
                {analyzedBy ? `${analyzedBy.toUpperCase()} ANALYSIS` : "AI INTERPRETATION"}
              </span>
            )}
            <p style={{ marginTop: 8, fontSize: "0.74rem", color: "#606060" }}>
              <span style={{ fontWeight: 700 }}>Report Date</span> {reportDate}
            </p>
          </div>
          <div style={{ textAlign: "right" }}>
            <p style={{ fontSize: 18, fontWeight: 700, color: "#606060", margin: 0 }}>{title}</p>
            {subtitle && <p style={{ fontSize: 12, color: "#606060", margin: 0 }}>{subtitle}</p>}
          </div>
        </div>

        {/* Patient info card */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, padding: "16px 20px" }}>
          <div style={{ display: "flex", flex: 1, minWidth: 260, gap: 12, borderRadius: 12, background: "#f3f3f3", padding: 16 }}>
            <span style={{ display: "flex", height: 64, width: 64, flexShrink: 0, alignItems: "center", justifyContent: "center", borderRadius: "50%", background: "#dcdcdc", fontSize: 24, color: "#9a9a9a" }}>
              👤
            </span>
            <div>
              <p style={{ marginBottom: 4, fontSize: "0.82rem", fontWeight: 700, color: "#333" }}>Patient Information</p>
              <div style={{ display: "grid", gridTemplateColumns: "max-content 1fr", columnGap: 12, rowGap: 2, fontSize: "0.76rem", color: "#606060" }}>
                <span>Patient Name</span><span>{patient?.name || "—"}</span>
                <span>Date of Birth</span><span>{patient?.date_of_birth || "—"}</span>
                <span>Ethnicity</span><span>{patient?.ethnicity || "—"}</span>
                <span>Gender</span><span>{patient?.gender || "—"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Body — results summary, tables, AI summary box */}
        <div style={{ padding: "0 20px 8px" }}>{children}</div>

        {/* Interpretation / Methodology / Disclaimer — no background color on
            this wrapper on purpose: each card below carries its own white
            background/border, so a colored wrapper here only ever shows up
            as a stray grey rectangle when print pagination pushes a card to
            the next page but leaves this div's background behind. */}
        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 16, padding: "24px 20px" }}>
          <div style={innerCard}>
            <div style={sectionBar}>{interpretationTitle}</div>
            {interpretationNode}
          </div>

          {methodology && (
            <div style={innerCard}>
              <div style={sectionBar}>Methodology</div>
              <div style={textBlock}>{methodology}</div>
            </div>
          )}

          {disclaimer && (
            <div style={innerCard}>
              <div style={sectionBar}>Disclaimer</div>
              <div style={textBlock}>{disclaimer}</div>
            </div>
          )}

          {references && references.length > 0 && (
            <div style={innerCard}>
              <div style={sectionBar}>References</div>
              <ReferencesList items={references} />
            </div>
          )}

        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, borderTop: "1px solid #e2e2e2", background: "#fafafa", padding: "12px 20px" }}>
        <button
          onClick={handlePrint}
          style={{
            borderRadius: 999, background: "#0a0e27", padding: "6px 16px",
            fontSize: 12, fontWeight: 600, color: "#fff", border: "none", cursor: "pointer",
          }}
        >
          Download / Print PDF
        </button>
      </div>
    </div>
  );
}
